<script setup>
import { ref, computed } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid, DxColumn } from 'devextreme-vue/data-grid'
import { DxSelectBox } from 'devextreme-vue/select-box'
import { DxPopup } from 'devextreme-vue/popup'
import { DxForm } from 'devextreme-vue/form'

import * as dataGrid from '@/composables/data-grid'
import { codeNameDisplayExpr } from '@/composables/label'

import { useAccountingStore } from '@/stores/accounting.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useProductFamilyColumn } from '@/composables/data-grid-column'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import GridLayoutConfigTemplate from '@/components/shared/GridLayoutConfigTemplate.vue'

import { useEntityLock } from '@/services/entity-lock.service'
import { registryApi } from '@/apis/registry.api'

const gridId = 'supplier-brand-grid'

const i18n = getI18n()
const { gridRef } = dataGrid.useGridRef()
const { route, router } = dataGrid.useGridRouter()

const accountingStore = useAccountingStore()
const registryStore = useRegistryStore()
const webSiteStore = useWebSiteStore()

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.supplierBrandsUrl)

const entityLock = useEntityLock({
  baseLockUrl: registryStore.api.getAbsoluteUrl() + registryStore.api.supplierBrandsUrl,
  modelName: 'SupplierBrand',
  gridRef,
})

const rowButtons = entityLock.getEntityLockButtons({
  visible: {
    delete: (e) => !e.isReadonly && !entityLock.isLocked(false, e.entityId)
  }
})

const isAccountingModuleActive = (webSiteStore.hasClaim(webSiteStore.claimsEnum.ACCOUNTING_READ) || true)
const canMergeSupplierBrands = webSiteStore.hasClaim(webSiteStore.claimsEnum.REGISTRY_MERGE_SUPPLIER_BRANDS)
const currentTradeAccountId = ref(null)
const currentFinancialCenterId = ref(null)
const editors = {}

const showWizard = ref(false)
const step = ref(1)
const sourceSupplierBrand = ref(null)
const destinationSupplierBrand = ref(null)
const missingProductCodes = ref([])
const productActions = ref({})

function openWizard() {
  step.value = 1
  sourceSupplierBrand.value = null
  destinationSupplierBrand.value = null
  missingProductCodes.value = []
  productActions.value = {}
  showWizard.value = true
}

const popupToolbar = computed(() => {
  const items = [
    {
      widget: 'dxButton',
      toolbar: 'bottom',
      location: 'after',
      options: {
        text: i18n.t('common.cancel'),
        onClick: () => {
          showWizard.value = false
        },
      },
    },
  ]

  switch (step.value) {
    case 1:
      items.unshift({
        widget: 'dxButton',
        toolbar: 'bottom',
        location: 'after',
        options: {
          text: i18n.t('common.next'),
          type: 'default',
          disabled: sourceSupplierBrand.value === null || destinationSupplierBrand.value === null,
          onClick: () => {
            registryApi.mergeSupplierBrandsAsync({
              sourceSupplierBrand: sourceSupplierBrand.value,
              destinationSupplierBrand: destinationSupplierBrand.value,
              verify: true
            }).then(response => {
              missingProductCodes.value = response.data.response || []
              step.value = 2
            })
          },
        },
      })
      break

    case 2:
      items.unshift(
        {
          widget: 'dxButton',
          toolbar: 'bottom',
          location: 'after',
          options: {
            text: i18n.t('common.back'),
            stylingMode: 'text',
            onClick: () => {
              step.value = 1
              missingProductCodes.value = []
              productActions.value = {}
            },
          },
        },
        {
          widget: 'dxButton',
          toolbar: 'bottom',
          location: 'after',
          options: {
            text: i18n.t('common.save'),
            type: 'default',
            disabled: sourceSupplierBrand.value === null || destinationSupplierBrand.value === null,
            onClick: () => {
              const actions = Object.fromEntries(
                missingProductCodes.value.map(code => [
                  code,
                  productActions.value[code] ?? 0
                ])
              )
              registryApi.mergeSupplierBrandsAsync({
                sourceSupplierBrand: sourceSupplierBrand.value,
                destinationSupplierBrand: destinationSupplierBrand.value,
                verify: false,
                actions,
              }).then(() => {
                showWizard.value = false
              })
            },
          },
        },
      )
      break
  }

  return items
})

const formItems = [
  {
    itemType: 'group',
    caption: i18n.t('common.priceListSelection'),
    colCount: 3,
    items: [
      {
        dataField: 'supplier_brand_ref',
        label: { text: `${i18n.t('common.priceListLocal')}` },
        editorType: 'dxSelectBox',
        editorOptions: {
          dataSource: {
            store: registryStore.supplierBrands,
            sort: 'code',
            filter: ['is_portal', '=', false],
          },
          valueExpr: 'id',
          displayExpr: codeNameDisplayExpr,
          searchEnabled: true,
          showClearButton: true,
          onValueChanged: (e) => {
            sourceSupplierBrand.value = e.value
          },
        },
      },
      { itemType: 'empty' },
      {
        dataField: 'supplier_brand_ref',
        label: { text: `${i18n.t('common.priceListOfficial')}` },
        editorType: 'dxSelectBox',
        editorOptions: {
          dataSource: {
            store: registryStore.supplierBrands,
            sort: 'code',
            filter: ['is_portal', '=', true],
          },
          valueExpr: 'id',
          displayExpr: codeNameDisplayExpr,
          searchEnabled: true,
          showClearButton: true,
          onValueChanged: (e) => {
            destinationSupplierBrand.value = e.value
          },
        },
      },
    ]
  },
]

const getFilteredTradeAccounts = (options) => {
  return {
    store: accountingStore.getLocalizedTradeAccounts(),
    filter: (item) => {
      return (item.type === accountingStore.tradeAccountTypesEnum.PURCHASE_SALE || item.type === accountingStore.tradeAccountTypesEnum.SALE) &&
        accountingStore.getTradeAccountIdsByFinancialCenterId(currentFinancialCenterId.value).includes(item.id)
    },
    sort: 'code',
  }
}

const getFilteredFinancialCenters = (options) => {
  return {
    store: accountingStore.getLocalizedFinancialCenters(),
    filter: (item) => {
      return (item.type === accountingStore.financialCenterTypesEnum.COST_INCOME || item.type === accountingStore.financialCenterTypesEnum.INCOME) &&
        accountingStore.getFinancialCenterIdsByTradeAccountId(currentTradeAccountId.value).includes(item.id)
    },
    sort: 'code',
  }
}

const accountingColumns = () => {
  if (isAccountingModuleActive) {
    return [
      {
        caption: i18n.t('common.tradeAccountSale'),
        dataField: 'trade_account_ref',
        width: '10rem',
        lookup: dataGrid.useLookup({ dataSource: getFilteredTradeAccounts }),
        setCellValue: (newData, value) => {
          newData = newData || {}
          newData.trade_account_ref = value
          currentTradeAccountId.value = value
          if (value && currentFinancialCenterId.value) {
            if (!accountingStore.getFinancialCenterIdsByTradeAccountId(value).includes(currentFinancialCenterId.value)) {
              currentFinancialCenterId.value = null
              newData.financial_center_ref = null
            }
          }
        },
      }, {
        caption: i18n.t('common.financialCenterIncome'),
        dataField: 'financial_center_ref',
        width: '10rem',
        lookup: dataGrid.useLookup({ dataSource: getFilteredFinancialCenters }),
        setCellValue: (newData, value) => {
          newData = newData || {}
          newData.financial_center_ref = value
          currentFinancialCenterId.value = value
          if (value && currentTradeAccountId.value) {
            if (!accountingStore.getTradeAccountIdsByFinancialCenterId(value).includes(currentTradeAccountId.value)) {
              currentTradeAccountId.value = null
              newData.trade_account_ref = null
            }
          }
        },
      }
    ]
  }
  return []
}

const columns = [
  {
    type: 'buttons',
    buttons: [{template: 'editButtonsTemplate'}],
    width: '4rem',
    visibleIndex: 0,
    fixed: false
  },
  { dataField: 'code', caption: i18n.t('common.supplierBrand'), sortOrder: 'asc', width: '4rem', validationRules: [{ type: 'required' }] },
  { dataField: 'name', caption: i18n.t('common.description'), validationRules: [{ type: 'required' }] },
  useProductFamilyColumn(),
  {
    caption: i18n.t('common.isManufacturer'),
    dataField: 'is_manufacturer',
    dataType: 'boolean',
    width: '10rem',
  },
  {
    caption: i18n.t('common.hasBrandCheck'),
    dataField: 'has_brand_check',
    dataType: 'boolean',
    width: '10rem',
  },
  {
    caption: i18n.t('common.printLabel'),
    dataField: 'is_label_mandatory',
    dataType: 'boolean',
    width: '10rem',
  },
  {
    caption: i18n.t('common.printBarcode'),
    dataField: 'is_barcode_mandatory',
    dataType: 'boolean',
    width: '10rem',
  },
  {
    caption: i18n.t('common.chargePercentage'),
    dataField: 'charge_percentage',
    dataType: 'number',
    width: '10rem',
    ...dataGrid.usePercentageOptions(),
  },
  {
    caption: i18n.t('common.priceListReleaseDate'),
    dataField: 'price_list_release_date',
    dataType: 'date',
    width: '10rem',
  },
  {
    caption: i18n.t('common.branch'),
    dataField: 'branch_ref',
    lookup: dataGrid.useLookup({ store: registryStore.getLocalizedBranches() }),
    validationRules: [{ type: 'required' }],
    width: '10rem',
  },
  ...accountingColumns()
]

const { defaultDialogTitle } = dataGrid.useDefaultDialogTitle()

const editing = dataGrid.useDefaultEditing(i => defaultDialogTitle(i, i18n.t('common.supplierBrand')), {
  colCount: 2,
  items: [
    {
      itemType: 'group',
      caption: i18n.t('common.mainInfo'),
      colSpan: 2,
      colCount: 8,
      items: [
        { dataField: 'code', editorOptions: dataGrid.useCodeFieldEditorOptions({ minLength: 3 }),
          colSpan: 2 },
        { dataField: 'name', colSpan: 2 },
        { dataField: 'product_family_ref', colSpan: 2 },
        { dataField: 'is_manufacturer' },
        { dataField: 'has_brand_check' },
      ]
    },
    {
      itemType: 'group',
      caption: i18n.t('common.priceList'),
      colCount: 2,
      items: [
          { dataField: 'charge_percentage', editorType: 'dxNumberBox', editorOptions: dataGrid.usePercentageOptions() },
          { dataField: 'price_list_release_date', editorType: 'dxDateBox', editorOptions: { type: 'date' } },
      ]
    },
    {
      itemType: 'group',
      caption: i18n.t('common.print'),
      colCount: 8,
      items: [
        { dataField: 'is_label_mandatory', colSpan: 2 },
        { dataField: 'is_barcode_mandatory', colSpan: 2 },
      ]
    },
    {
      itemType: 'group',
      caption: i18n.t('common.branch'),
      colCount: 2,
      items: [
        { dataField: 'branch_ref' },
      ]
    },
    {
      itemType: 'group',
      caption: i18n.t('common.accounting'),
      colCount: 2,
      visible: isAccountingModuleActive,
      items: [
        { dataField: 'trade_account_ref', editorOptions: { showClearButton: true } },
        { dataField: 'financial_center_ref', editorOptions: { showClearButton: true } },
      ]
    },
  ]
}, {
  allowDeleting: (e) => !e.row.data.is_portal,
  onInitializedCustom: (e) => entityLock.readOnlyToolbarItems(e),
  entityLock: entityLock
})

const onSaved = async(e) => {
  await registryStore.fetchSupplierBrandsAsync(true)
}

const onRowRemoved = () => {
  const gridInstance = gridRef.value?.instance
  gridInstance?.refresh()
}

const onEditorPreparing = (e) => {
  const defaultValueChanged = e.editorOptions.onValueChanged

  if (e.parentType === 'dataRow' && e.dataField === 'code') {
    e.editorOptions.readOnly = e.row.data?.id > 0
  }
  if (e.parentType === 'dataRow' && e.dataField === 'trade_account_ref') {
    currentTradeAccountId.value = e.row?.data?.trade_account_ref
    e.editorOptions.onInitialized = (e) => { editors['trade_account_ref'] = e.component }
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      editors['financial_center_ref'].option('dataSource', getFilteredFinancialCenters())
    }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'financial_center_ref') {
    currentFinancialCenterId.value = e.row?.data?.financial_center_ref
    e.editorOptions.onInitialized = (e) => { editors['financial_center_ref'] = e.component }
    e.editorOptions.onValueChanged = (args) => {
      defaultValueChanged(args)
      editors['trade_account_ref'].option('dataSource', getFilteredTradeAccounts())
    }
  }
}

const onInitNewRow = (e) => {
  if (!e.data?.branch_ref) {
    e.data.branch_ref = registryStore.getLocalizedBranches().find(b => b.code === '0')?.id
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: editing,
  onEditorPreparing: onEditorPreparing,
  onInitNewRow: onInitNewRow,
  onSaved: onSaved,
  onRowRemoved: onRowRemoved,
  onToolbarPreparing: (e) => {
    dataGrid.useDefaultToolbar(e, {
      addButtonOptions: { hint: `${i18n.t('common.add')} ${i18n.t('common.supplierBrand')}` },
    })
    e.toolbarOptions.items.unshift({
      locateInMenu: 'auto',
      location: 'after',
      widget: 'dxButton',
      options: {
        icon: 'mdi mdi-merge',
        hint: `${i18n.t('common.merge')} ${i18n.t('common.priceList', 2)}`,
        onClick: openWizard,
        disabled: !canMergeSupplierBrands,
      },
    })
    e.toolbarOptions.items.unshift(dataGrid.gridLayoutConfigToolbarItem)
  },
  routerFilter: {
    fields: [{series: 'series'}, {number: 'number_mnemonic'}],
    getRoute: () => route,
    router: router
  }
})

</script>
<template>
  <div>
    <dx-data-grid
      ref="gridRef"
      v-bind="gridOptions"
    >
      <template #gridLayoutConfigTemplate>
        <grid-layout-config-template
          :grid-ext-ref="gridRef"
          :grid-ext-id="gridId"
        />
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'SupplierBrand'"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="entityLock"
          :is-locked-available="true"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          :grid="gridRef"
          :entity-lock="entityLock"
        />
      </template>
    </dx-data-grid>
    <dx-popup
      v-model:visible="showWizard"
      :drag-enabled="true"
      :resize-enabled="true"
      :width="'80%'"
      height="auto"
      :title="`${i18n.t('common.merge')} ${i18n.t('common.priceList', 2)}`"
      :show-title="true"
      :toolbar-items="popupToolbar"
    >
      <template #content>
        <q-stepper
          v-model="step"
          flat
          animated
        >
          <q-step
            :name="1"
            :title="i18n.t('common.priceListSelection')"
            icon="mdi-percent"
            :done="step > 1"
          >
            <dx-form
              :items="formItems"
            />
          </q-step>

          <q-step
            :name="2"
            :title="i18n.t('common.productCodeValidation')"
            icon="mdi-format-list-bulleted"
            :done="step > 2"
          >
            <dx-data-grid
              :data-source="missingProductCodes.map(code => ({ code }))"
              :show-borders="true"
              :column-auto-width="true"
            >
              <dx-column
                data-field="action"
                :caption="i18n.t('common.action')"
                :width="'12rem'"
                cell-template="actionCellTemplate"
              />
              <dx-column
                data-field="code"
                :caption="i18n.t('common.code')"
              />
              <dx-column
                data-field="issue"
                :caption="i18n.t('common.validationMessage')"
                :calculate-cell-value="() => 'missing code'"
              />
              <template #actionCellTemplate="{ data }">
                <dx-select-box
                  :value="productActions[data.data.code] ?? 0"
                  :items="[
                    { text: i18n.t('common.cancel'), value: 0 },
                    { text: i18n.t('common.add'), value: 1 },
                  ]"
                  display-expr="text"
                  value-expr="value"
                  @value-changed="(e) => productActions[data.data.code] = e.value"
                />
              </template>
            </dx-data-grid>
          </q-step>
        </q-stepper>
      </template>
    </dx-popup>
  </div>
</template>
